// AiPromptComposer.jsx

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Context
import { useDatabase } from 'context/DatabaseContext';
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNavigation } from 'context/NavigationContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Utilities
import {
  filterMentionCandidates,
  getActiveMentionQuery,
  insertMentionToken,
  resolveInviteKeys,
  resolveMentionKeys,
  syncMentionTokens,
} from 'channels/utils/channelMentions';
import {
  buildNodeMentionCandidates,
  getActiveNodeMentionQuery,
  insertNodeMentionToken,
  mergeNodeReferences,
  resolveNodeReferencesFromMentionTokens,
  stripNodeMentionFromText,
  syncNodeMentionTokens,
} from 'channels/utils/channelNodeMentions';
import { hexToRgba } from 'utilities/Colors';
import { revokeObjectUrl } from 'utilities/Images';
import { getNodeTypeLabelFromDefinition } from 'utilities/NodeTypeLabels';
import { notifyBaseLimitError } from 'utilities/baseLimitFeedback';
import { searchBaseNodes } from 'services/search/BaseNodeSearchService';

// Hooks
import useTopLevelNodeCreate from 'hooks/useTopLevelNodeCreate';
import useBaseEntitlements from 'hooks/useBaseEntitlements';

// CSS
import './AiPromptComposer.css';

// Icons
import Icon from 'svg/Icon';

// Components
import ChannelAttachModal from 'channels/components/ChannelAttachModal';
import ChannelMentionAutocomplete from 'channels/components/ChannelMentionAutocomplete';
import ChannelNodeMentionAutocomplete from 'channels/components/ChannelNodeMentionAutocomplete';
import AddModal from 'components/addmodal/AddModal';
import Button from 'components/button/Button';
import Confirm from 'components/confirm/Confirm';
import Divider from 'components/divider/Divider';
import CameraModal from 'editor/nodeadd/camera/CameraModal';

const SpeechRecognitionApi = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

const MIXED_ATTACH_TYPE_KEY = 'ATTACH';
const CAMERA_TYPE_KEY = 'CAMERA';

/**
 * Picks an img-ready preview URL from a node reference row.
 *
 * @param {object} [row]
 * @returns {string|null}
 */
function pickDirectPreviewUrl(row = {}) {
  const candidates = [
    row.previewUrl,
    row.localUri,
    row.url,
    row.url_thumb,
    row.url_grid,
  ];

  for (const value of candidates) {
    const url = String(value || '').trim();
    if (!url) continue;
    if (
      url.startsWith('blob:')
      || url.startsWith('data:')
      || url.startsWith('http://')
      || url.startsWith('https://')
    ) {
      return url;
    }
  }

  return null;
}

/**
 * Returns true when a node reference should render as a photo thumbnail.
 *
 * @param {object} [row]
 * @returns {boolean}
 */
function isPhotoPreviewRow(row = {}) {
  const nodeType = String(row?.nodeType || row?.type || '').trim().toUpperCase();
  return nodeType === 'PHOTO' && Boolean(row?.previewUrl);
}

/**
 * AiPromptComposer
 *
 * Shared prompt composer for Ai chat and Capture: text, voice, @/# mentions,
 * existing-node attach, file attach, and camera capture.
 *
 * @param {'channel'|'capture'} surface - Visual chrome (channel bar vs embedded Capture).
 * @param {boolean} isAi - Forces overlay action layout (also true for Capture).
 * @param {string} [placeholder] - Optional textarea placeholder override.
 * @param {string} [externalError] - Parent-owned error (e.g. Capture submit failure).
 * @param {function} [onPendingChange] - Notifies parent when draft text/attachments change.
 *
 * @returns {ReactNode} The rendered prompt composer.
 */
const AiPromptComposer = React.memo(({
  disabled,
  isSending,
  onSubmit,
  mode = 'channel',
  surface = 'channel',
  isAi = false,
  placeholder: placeholderProp = '',
  externalError = '',
  showAttachActions = true,
  mentionCandidates = [],
  channelMemberKeys = [],
  canInviteMembers = false,
  channelTitle = '',
  onInviteMembers,
  onPendingChange,
}) => {
  const useOverlayLayout = isAi || surface === 'capture';
  const isCaptureSurface = surface === 'capture';
  const attachActionsVisible = showAttachActions !== false;

  // ----- CONTEXT ----- //

  // Language Context

  const language = useCurrentLanguage();

  // Navigation Context
  const { setCurrentNode, setCurrentSection } = useNavigation();

  // Node Types Context
  const { getType } = useNodeTypes();

  // Theme Context
  const { theme } = useTheme();

  // UI Context
  const { setSelectedLocation } = useUI();

  // Database Context
  const { setActiveDatabase } = useDatabase();

  // User Context
  const { user } = useUser();

  // Base Context
  const { activeBase } = useBase();
  const baseKey = String(activeBase?.key || activeBase?.id || '').trim();

  const { entitlements: baseEntitlements } = useBaseEntitlements({
    isActive: isAi && Boolean(user?.key),
    baseKey,
  });

  // ----- ROUTING ----- //

  const navigate = useNavigate();

  // ----- STATE ----- //

  // Composer Text State

  const [text, setText] = useState('');

  // ----- REFS ----- //

  // Composer Input Ref

  const inputRef = useRef(null);

  // ----- STATE ----- //

  // Voice Input State

  const [isListening, setIsListening] = useState(false);
  const [voiceErrorMessage, setVoiceErrorMessage] = useState('');

  // ----- REFS ----- //

  const isListeningRef = useRef(false);
  const recognitionRef = useRef(null);
  const speechBaseTextRef = useRef('');
  const nodeReferencesRef = useRef([]);

  // ----- STATE ----- //

  // Attach Modal Open State
  const [attachOpen, setAttachOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Attached Node Reference State
  const [nodeReferences, setNodeReferences] = useState([]);

  // Attachment Creation State
  const [isCreatingAttachment, setIsCreatingAttachment] = useState(false);

  // Mention Composer State
  const [mentionTokens, setMentionTokens] = useState([]);
  const [nodeMentionTokens, setNodeMentionTokens] = useState([]);
  const [nodeMentionCandidates, setNodeMentionCandidates] = useState([]);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [inviteConfirmOpen, setInviteConfirmOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [inviteErrorMessage, setInviteErrorMessage] = useState('');

  // ----- MEMOS ----- //

  // Node reference preview rows with display type labels.

  const nodeReferencePreviewRows = useMemo(() => {
    return nodeReferences.map((row) => {
      const nodeType = String(row?.nodeType || 'NODE').trim() || 'NODE';
      const typeDef = getType(nodeType);

      return {
        ...row,
        previewUrl: row?.previewUrl || pickDirectPreviewUrl(row),
        typeLabel: getNodeTypeLabelFromDefinition(typeDef, nodeType, { fallback: 'Item' }),
      };
    });
  }, [nodeReferences, getType]);

  const photoPreviewRows = useMemo(
    () => nodeReferencePreviewRows.filter((row) => isPhotoPreviewRow(row)),
    [nodeReferencePreviewRows],
  );

  const listPreviewRows = useMemo(
    () => nodeReferencePreviewRows.filter((row) => !isPhotoPreviewRow(row)),
    [nodeReferencePreviewRows],
  );

  const usageGate = useMemo(() => {
    if (!isAi) return null;

    const allowance = baseEntitlements?.usageAllowance;
    const aiEnabled = allowance?.aiEnabled !== false
      && baseEntitlements?.features?.aiEnabled !== false;

    if (!aiEnabled) {
      return {
        blocked: true,
        limitType: 'ai_disabled',
        message: 'Ai is not included on this base plan.',
      };
    }

    if (
      allowance?.usageMeterEnabled === true
      && Number(allowance?.percentUsed || 0) >= 100
    ) {
      return {
        blocked: true,
        limitType: 'usage_allowance',
        used: allowance?.creditsUsed,
        limit: allowance?.creditsEffective,
        message: 'This base has used its included usage for this month.',
      };
    }

    return null;
  }, [isAi, baseEntitlements]);

  const usageBlocked = Boolean(usageGate?.blocked);

  // Composer can send when enabled and either text or attachments exist.
  // Usage-blocked states still allow submit so we can open the limit modal.
  const canSend = useMemo(() => {
    return !disabled && !isSending && (
      String(text || '').trim().length > 0 || nodeReferences.length > 0
    );
  }, [disabled, isSending, nodeReferences.length, text]);

  const attachControlsEnabled = attachActionsVisible
    && !(disabled || isSending || isCreatingAttachment);

  const resolvedPlaceholder = String(placeholderProp || '').trim() || (
    mode === 'thread'
      ? 'Reply in thread...'
      : (useOverlayLayout
        ? 'Ask about a note, task, or question… (@ people, # items, /about)'
        : 'Send a message...')
  );

  const composerErrorMessage = (
    inviteErrorMessage || voiceErrorMessage || usageGate?.message || externalError
  );

  const activePersonMentionQuery = useMemo(() => (
    getActiveMentionQuery(text, cursorPosition)
  ), [cursorPosition, text]);

  const activeNodeMentionQuery = useMemo(() => (
    getActiveNodeMentionQuery(text, cursorPosition)
  ), [cursorPosition, text]);

  const activeMentionKind = useMemo(() => {
    if (activePersonMentionQuery && activeNodeMentionQuery) {
      return activePersonMentionQuery.start >= activeNodeMentionQuery.start
        ? 'person'
        : 'node';
    }
    if (activePersonMentionQuery) return 'person';
    if (activeNodeMentionQuery) return 'node';
    return null;
  }, [activeNodeMentionQuery, activePersonMentionQuery]);

  const activeMentionQuery = activeMentionKind === 'person'
    ? activePersonMentionQuery
    : (activeMentionKind === 'node' ? activeNodeMentionQuery : null);

  const visiblePersonMentionCandidates = useMemo(() => (
    filterMentionCandidates(mentionCandidates, activePersonMentionQuery?.query)
  ), [activePersonMentionQuery?.query, mentionCandidates]);

  const visibleNodeMentionCandidates = nodeMentionCandidates;

  const visibleMentionCandidates = activeMentionKind === 'person'
    ? visiblePersonMentionCandidates
    : (activeMentionKind === 'node' ? visibleNodeMentionCandidates : []);

  const isMentionMenuOpen = Boolean(
    activeMentionKind
    && activeMentionQuery
    && visibleMentionCandidates.length > 0
  );

  const inviteConfirmMessage = useMemo(() => {
    if (!pendingSubmit?.inviteNames?.length) return '';

    const names = pendingSubmit.inviteNames.join(', ');
    const channelLabel = String(channelTitle || 'this channel').trim() || 'this channel';
    const subject = pendingSubmit.inviteNames.length === 1 ? names : 'These people';
    return `${subject} ${pendingSubmit.inviteNames.length === 1 ? 'is' : 'are'} not in ${channelLabel}. Would you like to add them so they can see your message, or send this message without adding them?`;
  }, [channelTitle, pendingSubmit]);

  // ----- EFFECTS ----- //

  useLayoutEffect(() => {
    const inputElement = inputRef.current;
    if (!inputElement) return;

    inputElement.style.height = 'auto';
    inputElement.style.height = `${inputElement.scrollHeight}px`;
  }, [text]);

  useEffect(() => {
    if (activeMentionKind !== 'node' || !activeNodeMentionQuery) {
      setNodeMentionCandidates([]);
      return undefined;
    }

    const baseKey = String(activeBase?.key || '').trim();
    const rawQuery = String(activeNodeMentionQuery.query || '').trim();

    if (!baseKey || !rawQuery) {
      setNodeMentionCandidates([]);
      return undefined;
    }

    let cancelled = false;

    const handle = setTimeout(async () => {
      try {
        const rows = await searchBaseNodes({
          rawQuery,
          baseKey,
          max: 40,
        });

        if (cancelled) return;

        setNodeMentionCandidates(buildNodeMentionCandidates(rows, (nodeType) => (
          getNodeTypeLabelFromDefinition(getType(nodeType), nodeType, { fallback: 'Item' })
        )));
        setMentionActiveIndex(0);
      } catch {
        if (cancelled) return;
        setNodeMentionCandidates([]);
      }
    }, 220);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [
    activeMentionKind,
    activeNodeMentionQuery,
    activeBase?.key,
    getType,
  ]);

  // ----- MEMOS ----- //

  /**
   * Appends a voice transcript fragment onto the speech baseline text.
   *
   * @param {string} baseText - Text captured when listening started (plus finals).
   * @param {string} transcript - Interim or final transcript fragment.
   * @returns {string} Combined composer text.
   */

  const joinVoiceTranscript = useCallback((baseText, transcript) => {
    const base = String(baseText || '');
    const next = String(transcript || '').trim();
    if (!next) return base;
    if (!base) return next;
    return /\s$/.test(base) ? `${base}${next}` : `${base} ${next}`;
  }, []);

  /**
   * Writes voice transcript text into the composer and keeps mentions in sync.
   *
   * @param {string} nextText - Full composer value after voice update.
   */
  const applyVoiceText = useCallback((nextText) => {
    setText(nextText);
    setCursorPosition(nextText.length);
    setMentionTokens((prev) => syncMentionTokens(nextText, prev));
    setNodeMentionTokens((prev) => syncNodeMentionTokens(nextText, prev));
    setMentionActiveIndex(0);
  }, []);

  /**
   * Stops an active speech recognition session.
   */
  const stopVoiceInput = useCallback(() => {
    isListeningRef.current = false;
    const recognition = recognitionRef.current;
    recognitionRef.current = null;

    if (recognition) {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try {
        recognition.stop();
      } catch {
        // Recognition may already be stopped.
      }
    }

    setIsListening(false);
  }, []);

  /**
   * Requests microphone access and starts speech-to-text into the composer.
   */
  const startVoiceInput = useCallback(async () => {
    setVoiceErrorMessage('');

    if (!SpeechRecognitionApi) {
      setVoiceErrorMessage('Voice input is not supported in this browser.');
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setVoiceErrorMessage('Microphone access is not available in this browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      const denied = error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError';
      setVoiceErrorMessage(
        denied
          ? 'Microphone permission was denied. Allow access to use voice input.'
          : 'Could not access the microphone. Check your device settings and try again.'
      );
      return;
    }

    const recognition = new SpeechRecognitionApi();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language === 'en' ? 'en-US' : (navigator.language || 'en-US');

    speechBaseTextRef.current = String(text || '');
    recognitionRef.current = recognition;
    isListeningRef.current = true;
    setIsListening(true);

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = result?.[0]?.transcript || '';
        if (!transcript) continue;

        if (result.isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        speechBaseTextRef.current = joinVoiceTranscript(
          speechBaseTextRef.current,
          finalTranscript
        );
      }

      applyVoiceText(joinVoiceTranscript(speechBaseTextRef.current, interimTranscript));
    };

    recognition.onerror = (event) => {
      if (event?.error === 'aborted' || event?.error === 'no-speech') return;

      const message = event?.error === 'not-allowed'
        ? 'Microphone permission was denied. Allow access to use voice input.'
        : 'Voice input stopped unexpectedly. Try again.';
      setVoiceErrorMessage(message);
      stopVoiceInput();
    };

    recognition.onend = () => {
      if (!isListeningRef.current || recognitionRef.current !== recognition) {
        setIsListening(false);
        return;
      }

      try {
        recognition.start();
      } catch {
        stopVoiceInput();
      }
    };

    try {
      recognition.start();
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    } catch {
      setVoiceErrorMessage('Could not start voice input. Try again.');
      stopVoiceInput();
    }
  }, [applyVoiceText, joinVoiceTranscript, language, stopVoiceInput, text]);

  // ----- HANDLERS ----- //

  /**
   * Toggles voice input listening on the composer field.
   */

  const handleVoiceToggle = useCallback(() => {
    if (disabled || isSending) return;

    if (isListening) {
      stopVoiceInput();
      return;
    }

    startVoiceInput();
  }, [disabled, isListening, isSending, startVoiceInput, stopVoiceInput]);

  // ----- EFFECTS ----- //

  useEffect(() => () => {
    stopVoiceInput();
  }, [stopVoiceInput]);

  useEffect(() => {
    nodeReferencesRef.current = nodeReferences;
  }, [nodeReferences]);

  useEffect(() => () => {
    (Array.isArray(nodeReferencesRef.current) ? nodeReferencesRef.current : []).forEach((row) => {
      if (row?.previewUrl) revokeObjectUrl(row.previewUrl);
    });
  }, []);

  useEffect(() => {
    onPendingChange?.({
      text,
      nodeReferences,
      hasAttachments: nodeReferences.length > 0,
      hasDraft: String(text || '').trim().length > 0 || nodeReferences.length > 0,
    });
  }, [nodeReferences, onPendingChange, text]);

  // ----- MEMOS ----- //

  /**
   * Merges incoming attachment rows by node key without duplicating entries.
   *
   * @param {array} rows - Candidate node rows to add to local references.
   */

  const upsertNodeReferences = useCallback((rows = []) => {
    setNodeReferences((prev) => {
      const nextByNodeKey = new Map();

      (Array.isArray(prev) ? prev : []).forEach((row) => {
        const nodeKey = String(row?.nodeKey || '').trim();
        if (!nodeKey) return;
        nextByNodeKey.set(nodeKey, row);
      });

      (Array.isArray(rows) ? rows : []).forEach((row) => {
        const nodeKey = String(row?.nodeKey || row?.key || '').trim();
        if (!nodeKey) return;

        const existing = nextByNodeKey.get(nodeKey);
        const previewUrl = pickDirectPreviewUrl(row) || existing?.previewUrl || null;

        nextByNodeKey.set(nodeKey, {
          nodeKey,
          title: String(row?.title || existing?.title || '').trim(),
          nodeType: String(row?.nodeType || row?.type || existing?.nodeType || '').trim() || 'NODE',
          previewUrl,
          url: String(row?.url || existing?.url || '').trim() || null,
          url_thumb: String(row?.url_thumb || existing?.url_thumb || '').trim() || null,
          url_grid: String(row?.url_grid || existing?.url_grid || '').trim() || null,
          storagePath: String(row?.storagePath || existing?.storagePath || '').trim() || null,
        });
      });

      return Array.from(nextByNodeKey.values());
    });
  }, []);

  // ----- HOOKS ----- //

  // Shared top-level node creation behaviors used by attach flows.

  const {
    cameraOpen,
    cameraContentType,
    uploadInputRef,
    pendingUploadAccept,
    pendingUploadMultiple,
    handleTopLevelActionSelect,
    handleUploadFileSelect,
    setCameraOpen,
    handleCameraContentCreated,
  } = useTopLevelNodeCreate({
    user,
    activeBaseKey: activeBase?.key || null,
    getType,
    language,
    navigate,
    setCurrentNode,
    setCurrentSection,
    setSelectedLocation,
    setActiveDatabase,
    shouldNavigate: false,
    onNodesPrepared: upsertNodeReferences,
    onNodesCreated: upsertNodeReferences,
  });

  // ----- HANDLERS ----- //

  /**
   * Opens Capture AddModal (typed create) or channel attach-existing modal.
   */
  const handleQuickAddClick = useCallback(() => {
    if (isCaptureSurface) {
      setAddModalOpen(true);
      return;
    }
    setAttachOpen(true);
  }, [isCaptureSurface]);

  /**
   * Handles a base type picked from Capture AddModal.
   *
   * @param {string} typeKey - Selected node type key.
   */
  const handleAddModalTypeSelect = useCallback(async (typeKey) => {
    const cleanKey = String(typeKey || '').trim();
    if (!cleanKey) return;

    setAddModalOpen(false);
    setIsCreatingAttachment(true);
    try {
      await handleTopLevelActionSelect(cleanKey, { shouldNavigate: true });
    } finally {
      setIsCreatingAttachment(false);
    }
  }, [handleTopLevelActionSelect]);

  /**
   * Creates a new top-level node from a selected system type and attaches it.
   *
   * @param {string} typeKey - The selected system type key.
   * @param {object} [options] - Extra create options (title, initialFields).
   * @returns {Promise<object|null>} The created node, when available.
   */
  const handleAttachType = useCallback(async (typeKey, options = {}) => {
    if (!typeKey) return null;

    setIsCreatingAttachment(true);

    try {
      const createdNode = await handleTopLevelActionSelect(typeKey, {
        shouldNavigate: false,
        ...options,
      });
      if (createdNode?.key) {
        upsertNodeReferences([createdNode]);
      }
      return createdNode || null;
    } finally {
      setIsCreatingAttachment(false);
    }
  }, [handleTopLevelActionSelect, upsertNodeReferences]);

  /**
   * Adds existing node selections from ChannelAttachModal to the attached reference list.
   *
   * @param {array} nodes - Selected existing node rows.
   */
  const handleAttachExisting = useCallback((nodes = []) => {
    upsertNodeReferences(nodes);
  }, [upsertNodeReferences]);

  /**
   * Removes a node reference from the attached reference list.
   *
   * @param {string} nodeKey - The node key to remove.
   */
  const handleRemoveReference = useCallback((nodeKey) => {
    const cleanKey = String(nodeKey || '').trim();
    if (!cleanKey) return;

    const mentionToken = nodeMentionTokens.find((token) => token?.nodeKey === cleanKey);

    setNodeReferences((prev) => {
      const removing = (Array.isArray(prev) ? prev : []).find((row) => row?.nodeKey === cleanKey);
      if (removing?.previewUrl) revokeObjectUrl(removing.previewUrl);
      return prev.filter((row) => row?.nodeKey !== cleanKey);
    });
    setNodeMentionTokens((prev) => prev.filter((token) => token?.nodeKey !== cleanKey));

    if (mentionToken?.title) {
      const nextText = stripNodeMentionFromText(text, mentionToken.title);
      setText(nextText);
      setMentionTokens((prev) => syncMentionTokens(nextText, prev));
    }
  }, [nodeMentionTokens, text]);

  // ----- MEMOS ----- //

  const resetComposer = useCallback(() => {
    stopVoiceInput();
    setText('');
    setNodeReferences([]);
    setMentionTokens([]);
    setNodeMentionTokens([]);
    setNodeMentionCandidates([]);
    setMentionActiveIndex(0);
    setPendingSubmit(null);
    setInviteConfirmOpen(false);
    setInviteErrorMessage('');
    setVoiceErrorMessage('');
  }, [stopVoiceInput]);

  const restoreComposer = useCallback(({
    submitText,
    submitNodeReferences,
    submitMentionTokens = [],
    submitNodeMentionTokens = [],
  }) => {
    setText(submitText);
    setNodeReferences(submitNodeReferences);
    setMentionTokens(submitMentionTokens);
    setNodeMentionTokens(submitNodeMentionTokens);
    setMentionActiveIndex(0);
  }, []);

  const finalizeSubmit = useCallback(async ({
    submitText,
    submitNodeReferences,
    submitMentionKeys,
    submitMentionTokens = [],
    submitNodeMentionTokens = [],
    inviteKeys = [],
  }) => {
    if (inviteKeys.length && canInviteMembers) {
      const invited = await onInviteMembers?.(inviteKeys);
      if (!invited) {
        setInviteErrorMessage('Could not add members to this channel. Try again or add them from channel settings.');
        return false;
      }
    }

    resetComposer();

    const sent = await onSubmit?.({
      text: submitText,
      nodeReferences: submitNodeReferences,
      mentionKeys: submitMentionKeys,
    });

    if (!sent) {
      restoreComposer({
        submitText,
        submitNodeReferences,
        submitMentionTokens,
        submitNodeMentionTokens,
      });
      if (!isCaptureSurface) {
        setInviteErrorMessage('Could not send your message. Try again.');
      }
      return false;
    }

    (Array.isArray(submitNodeReferences) ? submitNodeReferences : []).forEach((row) => {
      if (row?.previewUrl) revokeObjectUrl(row.previewUrl);
    });

    return true;
  }, [canInviteMembers, isCaptureSurface, onInviteMembers, onSubmit, resetComposer, restoreComposer]);

  // ----- HANDLERS ----- //

  /**
   * Submits the message payload with current text and attached references.
   *
   * @param {Event} event - The submit-like event from form, button, or key handling.
   */

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (usageBlocked && usageGate) {
      notifyBaseLimitError({
        error: {
          message: usageGate.message,
          details: {
            code: usageGate.limitType === 'ai_disabled'
              ? 'ai_disabled_for_plan'
              : 'base_usage_allowance_exhausted',
            limitType: usageGate.limitType,
            used: usageGate.used,
            limit: usageGate.limit,
            unit: 'credits',
          },
        },
        baseKey,
      });
      return;
    }

    if (!canSend) return;

    const syncedTokens = syncMentionTokens(text, mentionTokens);
    const syncedNodeTokens = syncNodeMentionTokens(text, nodeMentionTokens);
    const mentionKeys = resolveMentionKeys(syncedTokens);
    const inviteKeys = resolveInviteKeys(mentionKeys, channelMemberKeys);
    const submitNodeReferences = mergeNodeReferences(
      nodeReferences,
      resolveNodeReferencesFromMentionTokens(syncedNodeTokens),
    );

    if (inviteKeys.length && canInviteMembers) {
      const inviteNames = inviteKeys
        .map((userKey) => syncedTokens.find((token) => token.userKey === userKey)?.displayName)
        .filter(Boolean);

      setInviteErrorMessage('');
      setPendingSubmit({
        text,
        nodeReferences: submitNodeReferences,
        mentionKeys,
        mentionTokens: syncedTokens,
        nodeMentionTokens: syncedNodeTokens,
        inviteKeys,
        inviteNames,
      });
      setInviteConfirmOpen(true);
      return;
    }

    await finalizeSubmit({
      submitText: text,
      submitNodeReferences,
      submitMentionKeys: mentionKeys,
      submitMentionTokens: syncedTokens,
      submitNodeMentionTokens: syncedNodeTokens,
    });
  };

  /**
   * Handles add and send.
   */
  const handleAddAndSend = async () => {
    if (!pendingSubmit) return;

    const submitPayload = pendingSubmit;
    setInviteConfirmOpen(false);
    setPendingSubmit(null);

    await finalizeSubmit({
      submitText: submitPayload.text,
      submitNodeReferences: submitPayload.nodeReferences,
      submitMentionKeys: submitPayload.mentionKeys,
      submitMentionTokens: submitPayload.mentionTokens,
      submitNodeMentionTokens: submitPayload.nodeMentionTokens || [],
      inviteKeys: submitPayload.inviteKeys,
    });
  };

  /**
   * Handles send without adding.
   */
  const handleSendWithoutAdding = async () => {
    if (!pendingSubmit) return;

    const submitPayload = pendingSubmit;
    setInviteConfirmOpen(false);
    setPendingSubmit(null);

    await finalizeSubmit({
      submitText: submitPayload.text,
      submitNodeReferences: submitPayload.nodeReferences,
      submitMentionKeys: submitPayload.mentionKeys,
      submitMentionTokens: submitPayload.mentionTokens,
      submitNodeMentionTokens: submitPayload.nodeMentionTokens || [],
      inviteKeys: [],
    });
  };

  /**
   * Handles select person mention.
   *
   * @param {*} candidate
   */
  const handleSelectMention = useCallback((candidate) => {
    if (!candidate?.userKey || activeMentionKind !== 'person' || !activePersonMentionQuery) {
      return;
    }

    const nextValue = insertMentionToken(
      text,
      activePersonMentionQuery.start,
      activePersonMentionQuery.end,
      {
        userKey: candidate.userKey,
        displayName: candidate.name,
      }
    );

    setText(nextValue.text);
    setMentionTokens((prev) => syncMentionTokens(nextValue.text, [...prev, nextValue.token]));
    setNodeMentionTokens((prev) => syncNodeMentionTokens(nextValue.text, prev));
    setMentionActiveIndex(0);

    requestAnimationFrame(() => {
      const inputElement = inputRef.current;
      if (!inputElement) return;
      inputElement.focus();
      inputElement.setSelectionRange(nextValue.cursor, nextValue.cursor);
      setCursorPosition(nextValue.cursor);
    });
  }, [activeMentionKind, activePersonMentionQuery, text]);

  /**
   * Handles select node mention.
   *
   * @param {*} candidate
   */
  const handleSelectNodeMention = useCallback((candidate) => {
    if (!candidate?.nodeKey || activeMentionKind !== 'node' || !activeNodeMentionQuery) {
      return;
    }

    const nextValue = insertNodeMentionToken(
      text,
      activeNodeMentionQuery.start,
      activeNodeMentionQuery.end,
      {
        nodeKey: candidate.nodeKey,
        title: candidate.title,
        nodeType: candidate.nodeType,
      }
    );

    setText(nextValue.text);
    setNodeMentionTokens((prev) => (
      syncNodeMentionTokens(nextValue.text, [...prev, nextValue.token])
    ));
    setMentionTokens((prev) => syncMentionTokens(nextValue.text, prev));
    upsertNodeReferences([nextValue.token]);
    setNodeMentionCandidates([]);
    setMentionActiveIndex(0);

    requestAnimationFrame(() => {
      const inputElement = inputRef.current;
      if (!inputElement) return;
      inputElement.focus();
      inputElement.setSelectionRange(nextValue.cursor, nextValue.cursor);
      setCursorPosition(nextValue.cursor);
    });
  }, [activeMentionKind, activeNodeMentionQuery, text, upsertNodeReferences]);

  /**
   * Submits on Enter and allows newline with Shift+Enter.
   *
   * @param {KeyboardEvent} event - The textarea keyboard event.
   */
  const handleInputKeyDown = (event) => {
    if (isMentionMenuOpen) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setMentionActiveIndex((prev) => (
          (prev + 1) % visibleMentionCandidates.length
        ));
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setMentionActiveIndex((prev) => (
          (prev - 1 + visibleMentionCandidates.length) % visibleMentionCandidates.length
        ));
        return;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        const candidate = visibleMentionCandidates[mentionActiveIndex];
        if (activeMentionKind === 'node') {
          handleSelectNodeMention(candidate);
        } else {
          handleSelectMention(candidate);
        }
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        setNodeMentionCandidates([]);
        setCursorPosition(event.currentTarget.selectionStart || 0);
        return;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  /**
   * Handles input change.
   *
   * @param {*} event
   */
  const handleInputChange = (event) => {
    if (isListeningRef.current) {
      stopVoiceInput();
    }

    const nextText = event.target.value;
    setText(nextText);
    setCursorPosition(event.target.selectionStart || nextText.length);
    setMentionTokens((prev) => syncMentionTokens(nextText, prev));
    setNodeMentionTokens((prev) => syncNodeMentionTokens(nextText, prev));
    setMentionActiveIndex(0);
  };

  /**
   * Handles input select.
   *
   * @param {*} event
   */
  const handleInputSelect = (event) => {
    setCursorPosition(event.target.selectionStart || 0);
  };

  // ----- RENDER ----- //

  const quickAttachGroups = [
    {
      key: 'add',
      nodes: (
        <button
          className="ai-prompt-quick-type"
          type="button"
          aria-label={isCaptureSurface ? 'New' : 'Add'}
          title={isCaptureSurface ? 'New' : 'Add'}
          disabled={!attachControlsEnabled}
          onClick={handleQuickAddClick}
        >
          <Icon
            iconKey="add"
            width={18}
            height={18}
            color="currentColor"
          />
        </button>
      ),
    },
    {
      key: 'attach',
      nodes: (
        <button
          className="ai-prompt-quick-type"
          type="button"
          aria-label="Attach"
          title="Attach"
          disabled={!attachControlsEnabled}
          onClick={() => handleAttachType(MIXED_ATTACH_TYPE_KEY)}
        >
          <Icon
            iconKey="attach_file"
            width={18}
            height={18}
            color="currentColor"
          />
        </button>
      ),
    },
    {
      key: 'camera',
      nodes: (
        <button
          className="ai-prompt-quick-type"
          type="button"
          aria-label="Camera"
          title="Camera"
          disabled={!attachControlsEnabled}
          onClick={() => handleAttachType(CAMERA_TYPE_KEY)}
        >
          <Icon
            iconKey="camera"
            width={18}
            height={18}
            color="currentColor"
          />
        </button>
      ),
    },
  ];

  const quickAttachButtons = (
    <>
      {quickAttachGroups.map((group, index) => (
        <React.Fragment key={group.key}>
          {index > 0 ? (
            <Divider
              direction="vertical"
              height="18px"
              margin="4px"
            />
          ) : null}
          <div className="ai-prompt-quick-type-group">
            {group.nodes}
          </div>
        </React.Fragment>
      ))}
    </>
  );

  return (
    <form
      className={[
        'ai-prompt-container',
        useOverlayLayout ? 'ai-prompt-container--overlay' : '',
        isCaptureSurface ? 'ai-prompt-container--capture' : '',
      ].filter(Boolean).join(' ')}
      onSubmit={handleSubmit}
    >

      {photoPreviewRows.length > 0 ? (

        <div className="ai-prompt-thumb-grid">
          {photoPreviewRows.map((item) => (
            <div
              className="ai-prompt-thumb-item"
              key={item.nodeKey}
            >

              {/* THUMB IMAGE */}
              <img
                alt={item?.title || 'Photo'}
                className="ai-prompt-thumb-image"
                draggable={false}
                src={item.previewUrl}
              />

              {/* THUMB REMOVE */}
              <button
                aria-label="Remove photo"
                className="ai-prompt-thumb-remove"
                type="button"
                onClick={() => handleRemoveReference(item.nodeKey)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {listPreviewRows.length > 0 ? (

        <div className="ai-prompt-shared-list">
          {listPreviewRows.map((item) => (
            <div
              className="ai-prompt-shared-item"
              key={item.nodeKey}
            >

              {/* SHARED THUMB */}
              {item.previewUrl ? (
                <img
                  alt=""
                  className="ai-prompt-shared-thumb"
                  draggable={false}
                  src={item.previewUrl}
                />
              ) : null}

              {/* SHARED TEXT WRAP */}
              <div className="ai-prompt-shared-text-wrap">

                {/* SHARED TITLE */}
                <div
                  className="ai-prompt-shared-title"
                >
                  {item?.title || 'Untitled'}
                </div>

                {/* SHARED TYPE */}
                <div
                  className="ai-prompt-shared-type"
                >
                  {item?.typeLabel}
                </div>
              </div>

              {/* SHARED REMOVE */}
              <button
                className="ai-prompt-shared-remove"
                type="button"
                onClick={() => handleRemoveReference(item.nodeKey)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {/* INPUT */}
      <div className="ai-prompt-input-wrap">

        {/* ERROR */}
        {composerErrorMessage ? (
          <div
            className="ai-prompt-error"
            style={externalError && !inviteErrorMessage && !voiceErrorMessage ? {
              color: theme.error,
            } : undefined}
          >
            {composerErrorMessage}
          </div>
        ) : null}

        {isMentionMenuOpen && activeMentionKind === 'person' ? (
          <ChannelMentionAutocomplete
            candidates={visiblePersonMentionCandidates}
            activeIndex={mentionActiveIndex}
            onSelect={handleSelectMention}
          />
        ) : null}

        {isMentionMenuOpen && activeMentionKind === 'node' ? (
          <ChannelNodeMentionAutocomplete
            candidates={visibleNodeMentionCandidates}
            activeIndex={mentionActiveIndex}
            onSelect={handleSelectNodeMention}
          />
        ) : null}

        {/* INPUT FIELD */}
        <div className={`ai-prompt-input-field${useOverlayLayout ? ' ai-prompt-input-field--overlay' : ''}`}>

          {/* INPUT */}
          <textarea
            className={`ai-prompt-input${useOverlayLayout ? ' ai-prompt-input--overlay' : ''}`}
            ref={inputRef}
            placeholder={resolvedPlaceholder}
            rows={useOverlayLayout ? 3 : 2}
            value={text}
            disabled={disabled || isSending}
            onChange={handleInputChange}
            onClick={handleInputSelect}
            onKeyDown={handleInputKeyDown}
            onKeyUp={handleInputSelect}
          />

          {/* VOICE */}
          <button
            className={`ai-prompt-voice${isListening ? ' is-listening' : ''}`}
            style={{
              backgroundColor: isListening
                ? hexToRgba(theme.red, 0.18)
                : theme.background,
              color: isListening ? theme.red : theme.foregroundMuted,
            }}
            aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
            aria-pressed={isListening}
            disabled={disabled || isSending}
            title={isListening ? 'Stop voice input' : 'Voice input'}
            type="button"
            onClick={handleVoiceToggle}
          >
            <Icon
              iconKey="mic"
              width={16}
              height={16}
              color={isListening ? theme.red : theme.foregroundMuted}
            />
          </button>

          {useOverlayLayout ? (
            <div className="ai-prompt-input-actions">
              {attachActionsVisible ? (
                <div className="ai-prompt-input-actions-start" aria-label="Quick attach">
                  {quickAttachButtons}
                </div>
              ) : (
                <div className="ai-prompt-input-actions-start" />
              )}

              <button
                className="ai-prompt-icon-button ai-prompt-icon-button--send"
                type="button"
                aria-label={isSending ? 'Sending message' : 'Send message'}
                title={isSending ? 'Sending...' : 'Send'}
                disabled={!canSend}
                onClick={handleSubmit}
              >
                <Icon
                  iconKey="keyboard_return"
                  width={18}
                  height={18}
                  color="currentColor"
                />
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* ACTIONS */}
      {!useOverlayLayout && attachActionsVisible ? (
        <div className="ai-prompt-actions">

          <div className="ai-prompt-quick-types" aria-label="Quick attach">
            {quickAttachButtons}
          </div>

          <div className="ai-prompt-actions-end">
            <Button
              iconKey="send"
              label={isSending ? 'Sending...' : 'Send'}
              enabled={!(disabled || isSending || !canSend)}
              onClick={handleSubmit}
            />
          </div>

        </div>
      ) : null}

      {!useOverlayLayout && !attachActionsVisible ? (
        <div className="ai-prompt-actions">
          <div className="ai-prompt-actions-end">
            <Button
              iconKey="send"
              label={isSending ? 'Sending...' : 'Send'}
              enabled={!(disabled || isSending || !canSend)}
              onClick={handleSubmit}
            />
          </div>
        </div>
      ) : null}

      {attachActionsVisible ? (
        <>
          <ChannelAttachModal
            isOpen={attachOpen}
            baseKey={activeBase?.key || ''}
            excludeNodeKeys={nodeReferences.map((row) => row?.nodeKey).filter(Boolean)}
            onClose={() => setAttachOpen(false)}
            onAttach={handleAttachExisting}
          />

          {isCaptureSurface ? (
            <AddModal
              isOpen={addModalOpen}
              createOnly
              showFind={false}
              onClose={() => setAddModalOpen(false)}
              onTypeSelect={handleAddModalTypeSelect}
            />
          ) : null}

          <input
            style={{
              display: 'none',
            }}
            ref={uploadInputRef}
            type="file"
            accept={pendingUploadAccept}
            multiple={pendingUploadMultiple}
            onChange={handleUploadFileSelect}
          />

          <CameraModal
            isOpen={cameraOpen}
            setOpen={setCameraOpen}
            contentType={cameraContentType}
            onContentCreated={handleCameraContentCreated}
          />
        </>
      ) : null}

      <Confirm
        title="Add to channel?"
        message={inviteConfirmMessage}
        isOpen={inviteConfirmOpen}
        setOpen={(open) => {
          setInviteConfirmOpen(open);
          if (!open) {
            setPendingSubmit(null);
          }
        }}
        confirmLabel="Add and Send"
        secondaryLabel="Send Only"
        onConfirm={handleAddAndSend}
        onSecondary={handleSendWithoutAdding}
      />
    </form>
  );
});

// Dev Tools
AiPromptComposer.displayName = 'AiPromptComposer';

export default AiPromptComposer;
